// src/auth/Register.jsx
// --------------------------------------------------
// User registration UI
// --------------------------------------------------

import React, { useState, useCallback } from "react";
import { XIcon } from "../App";
import API_BASE_URL from "../config/api";
import { publish } from "../utils/eventBus";
import GoogleLoginButton from "../components/GoogleLoginButton";

/**
 * MessageModal
 * Reusable modal to show success or error messages (used for errors here)
 */
const MessageModal = ({ message, type, onClose }) => {
  if (!message) return null;

  const bgColor = type === "success" ? "bg-green-100" : "bg-red-100";
  const textColor = type === "success" ? "text-green-800" : "text-red-800";
  const borderColor = type === "success" ? "border-green-400" : "border-red-400";

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[100]">
      <div
        className={`relative p-6 rounded-lg shadow-xl max-w-sm w-full ${bgColor} ${textColor} border ${borderColor}`}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <XIcon className="w-5 h-5" />
        </button>
        <p className="text-center font-medium">{message}</p>
      </div>
    </div>
  );
};

const MIN_PASSWORD_LENGTH = 6;

const Register = ({ navigate, onLogin }) => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const usernameTimerRef = React.useRef(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");

  const [fieldErrors, setFieldErrors] = useState({});

  const validateEmail = (value) => {
    if (!value) return "Email is required";
    const re = /\S+@\S+\.\S+/;
    if (!re.test(value)) return "Enter a valid email";
    return null;
  };

  const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`;
    }
    return null;
  };

  const validateConfirm = (pass, confirm) => {
    if (!confirm) return "Confirm password is required";
    if (pass !== confirm) return "Passwords do not match";
    return null;
  };

  const runAllValidations = () => {
    const errors = {};
    if (!username) errors.username = 'Username is required';
    const e = validateEmail(email);
    const p = validatePassword(password);
    const c = validateConfirm(password, confirmPassword);

    if (e) errors.email = e;
    if (username && /[^a-zA-Z0-9_.-]/.test(username)) errors.username = 'Username contains invalid characters';
    if (p) errors.password = p;
    if (c) errors.confirmPassword = c;

    setFieldErrors(errors);
    return errors;
  };

  const checkUsernameAvailability = (value) => {
    if (!value) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    // debounce
    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    usernameTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/check-username?username=${encodeURIComponent(value)}`);
        const data = await res.json();
        setUsernameAvailable(Boolean(data.available));
      } catch (e) {
        console.error('username check error', e);
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 500);
  };

  React.useEffect(() => {
    return () => {
      if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    };
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();

    // reset previous messages
    setMessage("");
    setMessageType("error");

    // validation
    const errors = runAllValidations();
    if (Object.keys(errors).length > 0) {
      setMessage("Please fix the highlighted fields.");
      setMessageType("error");
      return;
    }

    setSubmitting(true);
    setFieldErrors({});

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, confirmPassword, username }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        setMessage(data.message || "Registration failed");
        setMessageType("error");
        return;
      }

      // Successful registration:
      // Save email for the Verify OTP page and navigate there immediately.
      localStorage.setItem("pendingVerifyEmail", email);

      // Navigate to OTP/verification screen (the VerifyOtp page should show a success banner)
      navigate("verifyOtp");

      // Clear form state
      setEmail("");
      setUsername("");
      setPassword("");
      setConfirmPassword("");
      setFieldErrors({});
    } catch (err) {
      console.error("Register error:", err);
      setMessage("Something went wrong. Please try again later.");
      setMessageType("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    setMessage("");
    setMessageType("error");
    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(data.message || "Google login failed");
        setMessageType("error");
        return;
      }

      localStorage.setItem("authToken", data.token);
      if (data.user?.username) localStorage.setItem('userName', data.user.username);
      if (data.user?.role) localStorage.setItem('userRole', data.user.role);

      const resolvedEmail = data.user?.email || email;
      if (onLogin) onLogin(resolvedEmail, data.user?.username);
      try { publish('auth:login', { email: resolvedEmail, username: data.user?.username }); } catch (e) { /* ignore */ }

      navigate("products");
    } catch (err) {
      console.error("Google login error:", err);
      setMessage("Something went wrong. Please try again later.");
      setMessageType("error");
    } finally {
      setSubmitting(false);
    }
  }, [email, navigate, onLogin]);

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h2 className="text-4xl font-bold text-center text-gray-800 mb-8 mt-4">
        Register
      </h2>

      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 max-w-md mx-auto">
        <div className="space-y-3">
          <GoogleLoginButton
            clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            onCredential={handleGoogleCredential}
            onError={(err) => {
              setMessage(err);
              setMessageType("error");
            }}
            text="continue_with"
          />
        </div>

        <div className="my-6 text-center text-gray-400">──────── OR ────────</div>

        <form onSubmit={handleRegister} className="space-y-6" noValidate>
          <div>
            <label className="block text-sm font-bold mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 ${
                fieldErrors.email ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.email && (
              <p className="text-sm text-red-600">{fieldErrors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Username</label>
            <input
              value={username}
              onChange={(e) => {
                const v = e.target.value.trim();
                setUsername(v);
                setUsernameAvailable(null);
                if (v && !/[^a-zA-Z0-9_.-]/.test(v)) checkUsernameAvailability(v);
              }}
              onBlur={(e) => checkUsernameAvailability(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 ${
                fieldErrors.username ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.username && (
              <p className="text-sm text-red-600">{fieldErrors.username}</p>
            )}
            {checkingUsername && <p className="text-xs text-gray-500">Checking...</p>}
            {usernameAvailable === true && <p className="text-xs text-green-600">Username available</p>}
            {usernameAvailable === false && <p className="text-xs text-red-600">Username taken</p>}
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 ${
                fieldErrors.password ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.password && (
              <p className="text-sm text-red-600">{fieldErrors.password}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 ${
                fieldErrors.confirmPassword ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.confirmPassword && (
              <p className="text-sm text-red-600">
                {fieldErrors.confirmPassword}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-full disabled:opacity-60"
          >
            {submitting ? "Registering..." : "Register"}
          </button>
        </form>

        {/* Keep MessageModal for errors */}
        {message && (
          <MessageModal
            message={message}
            type={messageType}
            onClose={() => setMessage("")}
          />
        )}

        <p className="text-center text-sm mt-6">
          Already have an account?{" "}
          <button
            onClick={() => navigate("login")}
            className="text-blue-600 underline"
          >
            Login here
          </button>
        </p>
      </div>
    </div>
  );
};

export default Register;
